import { resolvePosSyncWebsocketUrl } from '../api/posSyncApi';

const listeners = new Set();
let socket = null;
let currentScopeKey = null;
let connectionFactory = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let connectionGeneration = 0;

const clearReconnect = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const notifyListeners = (payload) => {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('Error in POS sync listener:', error);
    }
  });
};

const closeSocket = () => {
  if (!socket) return;
  try {
    socket.close();
  } catch (error) {
    console.error('Error closing POS sync socket:', error);
  }
  socket = null;
};

const scheduleReconnect = () => {
  clearReconnect();
  if (!currentScopeKey || typeof connectionFactory !== 'function') return;
  const delay = Math.min(15000, 1000 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    openSocket().catch((error) => {
      console.error('Error reconnecting POS sync socket:', error);
    });
  }, delay);
};

const openSocket = async () => {
  const generation = connectionGeneration;
  if (!currentScopeKey || typeof connectionFactory !== 'function') return;
  clearReconnect();
  notifyListeners({
    type: 'pos.sync.connection',
    connectionStatus: 'connecting',
    scopeKey: currentScopeKey,
  });

  let config;
  try {
    config = await connectionFactory();
  } catch (error) {
    if (generation !== connectionGeneration) return;
    notifyListeners({
      type: 'pos.sync.connection',
      connectionStatus: 'error',
      scopeKey: currentScopeKey,
      errorMessage: error?.response?.data?.message || error?.message || 'No se pudo conectar al POS compartido.',
    });
    scheduleReconnect();
    return;
  }

  if (generation !== connectionGeneration || !currentScopeKey) return;

  const websocketUrl = resolvePosSyncWebsocketUrl(config?.websocketUrl || '/ws-pos/live', config?.token);
  const nextSocket = new WebSocket(websocketUrl);
  socket = nextSocket;

  nextSocket.onopen = () => {
    if (socket !== nextSocket) return;
    reconnectAttempts = 0;
    notifyListeners({
      type: 'pos.sync.connection',
      connectionStatus: 'connected',
      scopeKey: currentScopeKey,
    });
  };

  nextSocket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);
      notifyListeners(payload);
    } catch (error) {
      console.error('Error parsing POS sync payload:', error);
    }
  };

  nextSocket.onerror = () => {
    notifyListeners({
      type: 'pos.sync.connection',
      connectionStatus: 'error',
      scopeKey: currentScopeKey,
      errorMessage: 'La conexión POS en tiempo real se interrumpió.',
    });
  };

  nextSocket.onclose = () => {
    if (socket === nextSocket) {
      socket = null;
    }
    notifyListeners({
      type: 'pos.sync.connection',
      connectionStatus: currentScopeKey ? 'disconnected' : 'idle',
      scopeKey: currentScopeKey,
    });
    if (currentScopeKey) {
      scheduleReconnect();
    }
  };
};

export const startPosSyncEvents = ({ scopeKey, getConnectionConfig } = {}) => {
  const normalizedScopeKey = String(scopeKey || '').trim();
  if (!normalizedScopeKey || typeof getConnectionConfig !== 'function') {
    stopPosSyncEvents();
    return;
  }

  connectionFactory = getConnectionConfig;
  if (currentScopeKey === normalizedScopeKey && socket) {
    return;
  }

  currentScopeKey = normalizedScopeKey;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  clearReconnect();
  closeSocket();
  openSocket().catch((error) => {
    console.error('Error opening POS sync socket:', error);
  });
};

export const stopPosSyncEvents = () => {
  currentScopeKey = null;
  connectionFactory = null;
  reconnectAttempts = 0;
  connectionGeneration += 1;
  clearReconnect();
  closeSocket();
  notifyListeners({
    type: 'pos.sync.connection',
    connectionStatus: 'idle',
    scopeKey: null,
  });
};

export const sendPosSyncEvent = (payload) => {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  try {
    socket.send(JSON.stringify(payload));
    return true;
  } catch (error) {
    console.error('Error sending POS sync payload:', error);
    return false;
  }
};

export const subscribePosSyncEvents = (listener) => {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
